import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import fileDownload from 'react-file-download';
import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import CopyableField from '~/components/Common/CopyableField';
import UserSearchWidget from '~/components/Common/UserSearchWidget';

import { useBulkUpdateUnusedVoucherUsersExpiry } from '~/hooks/apis/promo/useBulkUpdateUnusedVoucherUsersExpiry';
import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';
import { useExpireVoucherUser } from '~/hooks/apis/promo/useExpireVoucherUser';
import { useListVoucherUsers } from '~/hooks/apis/promo/useListVoucherUsers';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';
import useCurrentBrand from '~/hooks/useCurrentBrand';
import useDebounced from '~/hooks/useDebounced';

import { listVoucherUsers } from '~/services/PromoService';

import { ConvertToCSV } from '~/utils/arrayUtils';
import { getDefaultAddVoucherUsersExpiryDate, normalizeToISODate } from '~/utils/dateFormatter';
import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import GridPagination from '../Common/Elements/GridPagination';
import PageSubheader from '../Common/Elements/PageSubheader';
import DebugModal from '../DebugModal/DebugModal';

import BulkUploadVoucherUsersDialog from './BulkUploadVoucherUsersDialog';

type ExistingVoucherUser = definitions['VoucherUser'] & {
  amountUsed?: number;
};

function formatShortId(id: string | undefined, visibleChars = 8): string {
  if (!id) {
    return '—';
  }
  return id.length <= visibleChars ? id : `${id.slice(0, visibleChars)}…`;
}

type Props = {
  userIds: Array<string>;
  onChange: (userIds: Array<string>) => void;
  expiredAt: string;
  onExpiredAtChange: (expiredAt: string) => void;
  /** Voucher form fields — used when resolving empty add-user expiry (same rules as initial default). */
  voucherUserExpiresAt?: string;
  voucherUserExpiresInDays?: string;
  disabled?: boolean;
  voucherId?: string;
  /** Exact voucher name for bulk expiry API (matches saved voucher name). */
  voucherName?: string;
  showAdvanceDescription?: boolean;
};

export default function VoucherUsers({
  userIds,
  onChange,
  expiredAt,
  onExpiredAtChange,
  voucherUserExpiresAt = '',
  voucherUserExpiresInDays = '',
  showAdvanceDescription,
  disabled = false,
  voucherId,
  voucherName = '',
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const brand = useCurrentBrand();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearchValue, setUserSearchValue] = useState('');
  const [expireTarget, setExpireTarget] = useState<ExistingVoucherUser | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
  const [allowVoucherReuse, setAllowVoucherReuse] = useState(false);
  const [reactivateExpiredVouchers, setReactivateExpiredVouchers] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [bulkUnusedExpiryAt, setBulkUnusedExpiryAt] = useState(() => dayjs().add(1, 'year').format('YYYY-MM-DD'));
  const [bulkExpiryConfirmOpen, setBulkExpiryConfirmOpen] = useState(false);
  const [userIdTableFilter, setUserIdTableFilter] = useState('');
  const [listFilterSearchValue, setListFilterSearchValue] = useState('');
  const debouncedUserIdTableFilter = useDebounced(userIdTableFilter.trim(), 400);

  const listVoucherUsersQuery = useMemo(
    () => ({
      page: paginationModel.page,
      limit: paginationModel.pageSize,
      includeExpired: true as const,
      includeUsed: true as const,
      ...(debouncedUserIdTableFilter ? { user_id: debouncedUserIdTableFilter } : {}),
    }),
    [debouncedUserIdTableFilter, paginationModel.page, paginationModel.pageSize],
  );

  const { data: existingUsersResponse, isLoading: isExistingLoading } = useListVoucherUsers({
    voucherId,
    query: listVoucherUsersQuery,
  });

  const existingUsers = existingUsersResponse?.result?.voucherUsers ?? [];
  const existingUsersTotal = Number(existingUsersResponse?.result?.total ?? 0);
  const existingUserIds = useMemo(() => {
    return Array.from(new Set(existingUsers.map((user) => user.userId).filter(Boolean))).sort();
  }, [existingUsers]);
  const { data: usersSummaryMap, isLoading: isUsersSummaryLoading } = useGetUsersSummaryByIds({
    userIds: existingUserIds,
    brand,
  });
  const resolvedUsersSummaryMap = usersSummaryMap ?? new Map();

  const createVoucherUsersMutation = useCreateVoucherUsers();
  const expireVoucherUserMutation = useExpireVoucherUser();
  const bulkUpdateUnusedExpiryMutation = useBulkUpdateUnusedVoucherUsersExpiry();

  useEffect(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [debouncedUserIdTableFilter]);

  const handleAdd = useCallback(async () => {
    if (!selectedUserId) {
      enqueueSnackbar('Select a user to add', { variant: 'error' });
      return;
    }

    if (!voucherId) {
      onChange([...userIds, selectedUserId]);
      setSelectedUserId('');
      setUserSearchValue('');
      return;
    }

    try {
      const resolvedExpiredAt = expiredAt.trim()
        ? normalizeToISODate(expiredAt)
        : normalizeToISODate(
            getDefaultAddVoucherUsersExpiryDate({
              voucherUserExpiresAt,
              voucherUserExpiresInDays,
            }),
          );
      const response = await createVoucherUsersMutation.mutateAsync({
        voucher_id: voucherId,
        expired_at: resolvedExpiredAt,
        allow_voucher_reuse: allowVoucherReuse,
        reactivate_expired_vouchers: reactivateExpiredVouchers,
        voucher_users: [{ user_id: selectedUserId }],
      });
      const r = response.result;
      if (r?.skippedVoucherUsers?.length) {
        const reason = r.skippedVoucherUsers[0].reason === 'already_redeemed' ? 'already redeemed' : 'expired unused';
        enqueueSnackbar(`User was skipped — ${reason}`, { variant: 'warning' });
      } else if (r?.reactivatedVoucherUsers?.length) {
        enqueueSnackbar('Reactivated expired voucher user', { variant: 'success' });
      } else if (r?.updatedVoucherUsers?.length) {
        enqueueSnackbar('Updated existing voucher user (reuse)', { variant: 'success' });
      } else {
        enqueueSnackbar('Added voucher user', { variant: 'success' });
      }
      queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] });
      setSelectedUserId('');
      setUserSearchValue('');
    } catch (error) {
      enqueueSnackbar(`Failed to add voucher user: ${(error as Error).message}`, { variant: 'error' });
    }
  }, [
    allowVoucherReuse,
    createVoucherUsersMutation,
    enqueueSnackbar,
    onChange,
    queryClient,
    reactivateExpiredVouchers,
    selectedUserId,
    userIds,
    voucherId,
    expiredAt,
    voucherUserExpiresAt,
    voucherUserExpiresInDays,
  ]);

  const handleExpireConfirm = useCallback(async () => {
    if (!expireTarget) {
      return;
    }
    try {
      await expireVoucherUserMutation.mutateAsync(expireTarget.voucherUserId);
      enqueueSnackbar('Expired voucher user', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] });
    } catch (error) {
      enqueueSnackbar(`Failed to expire voucher user: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setExpireTarget(null);
    }
  }, [enqueueSnackbar, expireTarget, expireVoucherUserMutation, queryClient, voucherId]);

  // Handle form submit (Enter key) to add user when a user is selected
  const handleFormSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (selectedUserId && !disabled && !createVoucherUsersMutation.isPending) {
        handleAdd();
      }
    },
    [selectedUserId, disabled, createVoucherUsersMutation.isPending, handleAdd],
  );

  const handleDownloadCsv = useCallback(async () => {
    if (!voucherId) {
      return;
    }

    setIsDownloading(true);
    try {
      const PAGE_SIZE = 200;
      let page = 0;
      let allUsers: Array<ExistingVoucherUser> = [];
      let hasMore = true;

      while (hasMore) {
        const response = await listVoucherUsers(voucherId, {
          page,
          limit: PAGE_SIZE,
          includeExpired: true,
          includeUsed: true,
          ...(debouncedUserIdTableFilter ? { user_id: debouncedUserIdTableFilter } : {}),
        });
        const users = response.result?.voucherUsers ?? [];
        allUsers = allUsers.concat(users);
        const total = response.result?.total ?? 0;
        hasMore = users.length > 0 && allUsers.length < total;
        page += 1;
      }

      const csvData = allUsers.map((user) => ({
        user_id: user.userId,
        created_at: user.createdAt ?? '',
        used_at: user.usedAt ?? '',
        expired_at: user.expiredAt ?? '',
      }));

      const csv = ConvertToCSV(csvData);
      fileDownload(csv, `voucher-users-${voucherId}.csv`);
      enqueueSnackbar(`Downloaded ${allUsers.length} voucher users`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to download voucher users: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setIsDownloading(false);
    }
  }, [voucherId, enqueueSnackbar, debouncedUserIdTableFilter]);

  const handleBulkExpiryOpen = useCallback(() => {
    const trimmedName = voucherName.trim();
    if (!trimmedName) {
      enqueueSnackbar('Set a voucher name before bulk-updating expiry', { variant: 'error' });
      return;
    }
    if (!brand) {
      enqueueSnackbar('Select a brand (tenant) before bulk-updating expiry', { variant: 'error' });
      return;
    }
    if (!bulkUnusedExpiryAt.trim()) {
      enqueueSnackbar('Choose a new expiry date', { variant: 'error' });
      return;
    }
    setBulkExpiryConfirmOpen(true);
  }, [brand, bulkUnusedExpiryAt, enqueueSnackbar, voucherName]);

  const handleBulkExpiryConfirm = useCallback(async () => {
    const trimmedName = voucherName.trim();
    if (!trimmedName || !voucherId || !brand) {
      setBulkExpiryConfirmOpen(false);
      return;
    }
    try {
      const response = await bulkUpdateUnusedExpiryMutation.mutateAsync({
        voucherName: trimmedName,
        expiredAt: normalizeToISODate(bulkUnusedExpiryAt),
        brand: brand as definitions['Promo Brands'],
      });
      const n = response.result?.updatedCount ?? 0;
      enqueueSnackbar(response.message || `Updated ${n} unused voucher user row(s)`, { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] });
      queryClient.invalidateQueries({ queryKey: ['voucher', voucherId] });
    } catch (error) {
      enqueueSnackbar(`Bulk update failed: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setBulkExpiryConfirmOpen(false);
    }
  }, [bulkUnusedExpiryAt, bulkUpdateUnusedExpiryMutation, brand, enqueueSnackbar, queryClient, voucherId, voucherName]);

  const handleBulkExpiryDialogClose = useCallback(
    (_event: object, _reason: 'backdropClick' | 'escapeKeyDown') => {
      if (bulkUpdateUnusedExpiryMutation.isPending) {
        return;
      }
      setBulkExpiryConfirmOpen(false);
    },
    [bulkUpdateUnusedExpiryMutation.isPending],
  );

  const existingColumns = useMemo<Array<GridColDef<ExistingVoucherUser>>>(
    () => [
      {
        field: 'userName',
        headerName: 'Name',
        flex: 1,
        renderCell: (params: GridRenderCellParams<ExistingVoucherUser>) =>
          resolvedUsersSummaryMap.get(params.row.userId)?.full_name ?? '-',
      },
      {
        field: 'userEmail',
        headerName: 'Email',
        flex: 1,
        renderCell: (params: GridRenderCellParams<ExistingVoucherUser>) => (
          <Link to={`/users/${params.row.userId}`} target="_blank" rel="noreferrer">
            {resolvedUsersSummaryMap.get(params.row.userId)?.email ?? '-'}
          </Link>
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        minWidth: 180,
        flex: 1,
        renderCell: (params) => DateFormatterDetailed({ date: params.value }),
      },
      {
        field: 'amountUsed',
        headerName: 'Amount Used',
        flex: 1,
        renderCell: (params) => params.row.amountUsed,
      },
      {
        field: 'usedAt',
        headerName: 'Completed At',
        flex: 1,
        renderCell: (params) => DateFormatterDetailed({ date: params.value, includeYear: true }),
      },
      {
        field: 'expiredAt',
        headerName: 'Expires At',
        flex: 1,
        renderCell: (params) => DateFormatterDetailed({ date: params.value, highlightIfPast: true, includeYear: true }),
      },
      {
        field: 'debug',
        headerName: 'Debug',
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => <DebugModal type="generic" title="Debug" data={params.row} />,
      },
      {
        field: 'userId',
        headerName: 'User ID',
        width: 124,
        minWidth: 124,
        renderCell: (params: GridRenderCellParams<ExistingVoucherUser>) => (
          <CopyableField
            value={params.row.userId}
            label={formatShortId(params.row.userId)}
            variant="body2"
            title={params.row.userId}
            sx={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}
          />
        ),
      },
      {
        field: 'voucherUserId',
        headerName: 'Voucher User ID',
        width: 132,
        minWidth: 132,
        renderCell: (params: GridRenderCellParams<ExistingVoucherUser>) => (
          <CopyableField
            value={params.row.voucherUserId}
            label={formatShortId(params.row.voucherUserId)}
            variant="body2"
            title={params.row.voucherUserId}
            sx={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}
          />
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<ExistingVoucherUser>) => (
          <IconButton
            aria-label="Expire voucher user"
            size="small"
            onClick={() => setExpireTarget(params.row)}
            disabled={disabled || expireVoucherUserMutation.isPending}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [disabled, expireVoucherUserMutation.isPending, resolvedUsersSummaryMap],
  );

  return (
    <Box id="users">
      <PageSubheader title={`Users (${existingUsersTotal})`}>
        {showAdvanceDescription && (
          <Box component="span" color="text.secondary" alignItems="left">
            Users that qualify for this voucher. The voucher is not editable if it has users associated.
          </Box>
        )}
      </PageSubheader>
      {!voucherId ? (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Create the voucher to add users.
        </Typography>
      ) : (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Add Voucher Users
          </Typography>
          <form onSubmit={handleFormSubmit}>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <TextField
                label="Expires At"
                type="date"
                value={expiredAt}
                onChange={(event) => onExpiredAtChange(event.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
                sx={{ minWidth: 240 }}
              />
              <Box sx={{ flex: 1 }}>
                <UserSearchWidget
                  id="voucher-user-search"
                  label="Name / Email / Customer Support Code / User ID"
                  formErrors={[]}
                  onChange={(_, value) => setSelectedUserId(value)}
                  passedAutocompleteValue={userSearchValue}
                  setPassedAutocompleteValue={setUserSearchValue}
                  disabled={disabled}
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                disabled={disabled || createVoucherUsersMutation.isPending}
              >
                Add users
              </Button>
            </Stack>
          </form>
          <Stack direction="row" spacing={2} mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowVoucherReuse}
                  onChange={(e) => setAllowVoucherReuse(e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Allow voucher reuse"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={reactivateExpiredVouchers}
                  onChange={(e) => setReactivateExpiredVouchers(e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Reactivate expired vouchers"
            />
          </Stack>
          <Stack direction="row" spacing={2} mb={2}>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setIsBulkUploadOpen(true)}
              disabled={disabled}
            >
              Bulk Upload CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCsv}
              disabled={isDownloading || existingUsersTotal === 0}
            >
              {isDownloading ? 'Downloading...' : 'Download CSV'}
            </Button>
          </Stack>
          <Box mt={2} mb={1}>
            <Typography variant="subtitle2" gutterBottom>
              Bulk update unused users&apos; expiry
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
              Applies a new expiry to all voucher user rows that are unused (not redeemed) for this voucher name.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                label="New expiry"
                type="date"
                value={bulkUnusedExpiryAt}
                onChange={(event) => setBulkUnusedExpiryAt(event.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={disabled || !voucherName.trim() || !brand}
                sx={{ minWidth: 240 }}
              />
              <Button
                variant="outlined"
                color="primary"
                startIcon={<EditCalendarIcon />}
                onClick={handleBulkExpiryOpen}
                disabled={disabled || bulkUpdateUnusedExpiryMutation.isPending || !voucherName.trim() || !brand}
              >
                Apply to all unused
              </Button>
            </Stack>
          </Box>
        </>
      )}

      {voucherId && (
        <Box mb={3} mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            Voucher users list
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }} mb={2}>
            <Box sx={{ flex: 1, minWidth: { sm: 280 } }}>
              <UserSearchWidget
                id="voucher-users-list-filter"
                label="Name / Email / Customer Support Code / User ID"
                formErrors={[]}
                onChange={(_id, value) => setUserIdTableFilter(value)}
                passedAutocompleteValue={listFilterSearchValue}
                setPassedAutocompleteValue={setListFilterSearchValue}
                disabled={disabled || !brand}
              />
              {!brand ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, ml: 1.75 }}>
                  Select a brand (tenant) to search users.
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, ml: 1.75 }}>
                  Choose a user to filter the list by user ID; filter updates after a short delay.
                </Typography>
              )}
            </Box>
            <Button
              variant="text"
              onClick={() => {
                setUserIdTableFilter('');
                setListFilterSearchValue('');
              }}
              disabled={!userIdTableFilter && !listFilterSearchValue}
              sx={{ mt: { xs: 0, sm: 1 } }}
            >
              Clear
            </Button>
          </Stack>
          <DataGrid
            autoHeight
            rows={existingUsers}
            columns={existingColumns}
            slots={{
              pagination: GridPagination,
            }}
            getRowId={(row) => row.voucherUserId}
            initialState={{ columns: { columnVisibilityModel: { createdAt: false } } }}
            disableRowSelectionOnClick
            loading={isExistingLoading || isUsersSummaryLoading}
            rowCount={existingUsersTotal}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            sx={{ backgroundColor: 'background.paper' }}
          />
        </Box>
      )}

      <Dialog open={Boolean(expireTarget)} onClose={() => setExpireTarget(null)}>
        <DialogTitle>Expire Voucher User</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Expire voucher access for user {expireTarget?.userId}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setExpireTarget(null)} disabled={expireVoucherUserMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExpireConfirm}
            disabled={expireVoucherUserMutation.isPending}
          >
            Expire
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkExpiryConfirmOpen}
        onClose={handleBulkExpiryDialogClose}
        disableEscapeKeyDown={bulkUpdateUnusedExpiryMutation.isPending}
      >
        <DialogTitle>Bulk update unused voucher users</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Set expiry to {bulkUnusedExpiryAt || '—'} (UTC midnight when given as a date only) for every{' '}
            <strong>unused</strong> voucher user row matching voucher name &quot;{voucherName.trim() || '—'}&quot;? This
            does not change redeemed rows.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setBulkExpiryConfirmOpen(false)}
            disabled={bulkUpdateUnusedExpiryMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBulkExpiryConfirm}
            disabled={bulkUpdateUnusedExpiryMutation.isPending}
          >
            Update expiry
          </Button>
        </DialogActions>
      </Dialog>

      {voucherId && (
        <BulkUploadVoucherUsersDialog
          open={isBulkUploadOpen}
          onClose={() => setIsBulkUploadOpen(false)}
          voucherId={voucherId}
          brand={brand}
        />
      )}
    </Box>
  );
}
